/**
 * Chamadas das rotas do banco de horas.
 *
 * Papel: aqui ficam a leitura do banco de horas inteiro e as gravações de
 * colaborador, de lançamento mensal e da importação do extrato.
 *
 * Como usar: quem chama são os hooks de `usar-banco-horas.ts`; nenhum
 * componente chama estas funções direto. `excluir…` não recebe schema porque a
 * API responde 204 sem corpo, e o parse falharia justamente no caminho de
 * sucesso.
 */
import {
  type BancoDeHoras,
  type ColaboradorBancoHoras,
  type ImportacaoBancoHoras,
  type LancamentoMensal,
  type NovoColaboradorBancoHoras,
  type NovoLancamentoMensal,
  type ResultadoImportacaoBancoHoras,
  bancoDeHorasSchema,
  colaboradorBancoHorasSchema,
  lancamentoMensalSchema,
  resultadoImportacaoBancoHorasSchema,
} from '@concierge/shared';

import { clienteApi } from '@/shared/api';

/**
 * Lê o banco de horas inteiro: a data de atualização e cada colaborador com os meses.
 *
 * @param sinal - O sinal de cancelamento da consulta.
 * @returns O banco de horas.
 */
export function buscarBancoDeHoras(sinal?: AbortSignal): Promise<BancoDeHoras> {
  return clienteApi.get('/banco-horas', { schema: bancoDeHorasSchema, sinal });
}

/**
 * Cadastra um colaborador no banco de horas.
 *
 * @param dados - O que a caixa de colaborador validou.
 * @returns O colaborador gravado, sem meses.
 */
export function criarColaboradorBancoHoras(
  dados: NovoColaboradorBancoHoras,
): Promise<ColaboradorBancoHoras> {
  return clienteApi.post('/banco-horas/colaboradores', dados, {
    schema: colaboradorBancoHorasSchema,
  });
}

/**
 * Altera um colaborador do banco de horas.
 *
 * @param id - O `id` na lista do banco de horas.
 * @param dados - O que a caixa de colaborador validou.
 * @returns O colaborador gravado, com os meses.
 */
export function atualizarColaboradorBancoHoras(
  id: string,
  dados: NovoColaboradorBancoHoras,
): Promise<ColaboradorBancoHoras> {
  return clienteApi.put(`/banco-horas/colaboradores/${encodeURIComponent(id)}`, dados, {
    schema: colaboradorBancoHorasSchema,
  });
}

/**
 * Remove um colaborador do banco de horas, com o histórico dele.
 *
 * @param id - O `id` na lista do banco de horas.
 * @returns Nada; a API responde 204.
 */
export function excluirColaboradorBancoHoras(id: string): Promise<void> {
  return clienteApi.remover(`/banco-horas/colaboradores/${encodeURIComponent(id)}`);
}

/**
 * Lança um mês.
 *
 * @param dados - O que a caixa do mês validou.
 * @returns O lançamento gravado.
 */
export function criarLancamentoMensal(dados: NovoLancamentoMensal): Promise<LancamentoMensal> {
  return clienteApi.post('/banco-horas/meses', dados, { schema: lancamentoMensalSchema });
}

/**
 * Altera os tempos e a observação de um mês; o período e a pessoa não mudam.
 *
 * @param id - O `id` do lançamento.
 * @param dados - O que a caixa do mês validou.
 * @returns O lançamento gravado.
 */
export function atualizarLancamentoMensal(
  id: string,
  dados: NovoLancamentoMensal,
): Promise<LancamentoMensal> {
  return clienteApi.put(`/banco-horas/meses/${encodeURIComponent(id)}`, dados, {
    schema: lancamentoMensalSchema,
  });
}

/**
 * Remove um mês.
 *
 * @param id - O `id` do lançamento.
 * @returns Nada; a API responde 204.
 */
export function excluirLancamentoMensal(id: string): Promise<void> {
  return clienteApi.remover(`/banco-horas/meses/${encodeURIComponent(id)}`);
}

/**
 * Aplica as linhas da importação do extrato.
 *
 * @param dados - As linhas novas e as que mudam, uma por pessoa e mês.
 * @returns Quantas linhas foram gravadas.
 */
export function importarBancoHoras(
  dados: ImportacaoBancoHoras,
): Promise<ResultadoImportacaoBancoHoras> {
  return clienteApi.post('/banco-horas/importacao', dados, {
    schema: resultadoImportacaoBancoHorasSchema,
  });
}
